import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { PageTitleDirective } from '@directives/page-title/page-title.directive';
import { OrderDetailsComponent } from '@features/shopping/order-details/order-details.component';
import { calculateBulkOrderItems } from '@models/functions.model';
import { NotificationResponse } from '@models/notifications.model';
import { ExistingAssetDetails, OrderDetailsSummary } from '@models/shopping-cart.model';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { LayoutMediatorService } from '@services/view/layout-mediator-service/layout-mediator.service';
import { Subscription } from 'rxjs';
import { DataLoaderCaptureOverrideDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-order-summary',
  templateUrl: './order-summary-index.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [CommonModule, PageTitleDirective, OrderDetailsComponent, NgbTooltipModule]
})
export class OrderSummaryIndexComponent implements OnInit, OnDestroy {
  page_title: string = '';
  navigation_title: string = '';
  order_id: number = 0;
  order_not_found: boolean = false;
  order_summary: OrderDetailsSummary;
  order_status_desc: string;
  order_number_txt: string;
  asset_details: ExistingAssetDetails;
  is_tmo_environment: boolean = false;
  private subscription: Subscription = new Subscription();

  constructor(
    private activated_route: ActivatedRoute,
    private layout_mediator: LayoutMediatorService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService,
    private session_svc: SessionService
  ) {}

  ngOnInit(): void {
    this.order_number_txt = this.translation_svc.translate('order_id');
    this.is_tmo_environment = this.session_svc.getTmoEnvironment();

    this.subscription.add(
      this.activated_route.paramMap.subscribe((params: ParamMap) => {
        this.order_id = 0;
        this.order_not_found = false;

        const order_id: number = Number(params.get('id'));
        this.setPageTitle();
        if (!isNaN(order_id) && order_id > 0) {
          this.shopping_svc.getOrderDetails(order_id).subscribe({
            next: (res: OrderDetailsSummary) => {
              this.order_summary = res;
              this.order_id = res.id;
              this.setPageTitle();

              if (this.order_summary.order_price_summary?.bulk_order_additional_info?.bulk_quantity)
                this.order_summary.order_price_summary = calculateBulkOrderItems(
                  this.order_summary.order_price_summary
                );

              this.asset_details = {
                existing_asset: this.order_summary?.existing_asset!,
                keep_plan: this.order_summary?.keep_plan,
                existing_person: this.order_summary.source_order.existing_person,
                replacement_user: this.order_summary.source_order.replacement_user
              };

              switch (this.order_summary.source_order.source_system_status) {
                case 'Denied':
                  this.getDenialNotification(order_id);
                  break;
                default:
                  this.getWhatNextNotification(order_id);
                  break;
              }
            },
            error: (err: HttpErrorResponse) => {
              this.orderNotFound();
            }
          });
        } else {
          this.orderNotFound();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setPageTitle(): void {
    this.page_title =
      this.order_id > 0 ? `${this.translation_svc.translate('order_id')}: ${this.order_id}` : '';
    this.navigation_title =
      this.order_id > 0 ? this.translation_svc.translate('order_summary') : '';
  }

  private orderNotFound(): void {
    this.order_not_found = true;
    this.page_title = '';
    this.navigation_title = this.translation_svc.translate('resource_error_title');
    this.layout_mediator.setError('resource');
  }

  private getWhatNextNotification(order_id: number): void {
    this.shopping_svc.getWhatNextNotification(order_id).subscribe({
      next: (res: NotificationResponse) => {
        this.order_status_desc = res.notifications[0]?.notification;
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  private getDenialNotification(order_id: number): void {
    this.shopping_svc.getDenialNotification(order_id).subscribe({
      next: (res: NotificationResponse) => {
        this.order_status_desc = res.notifications[0]?.notification;
      },
      error: (err: HttpErrorResponse) => {}
    });
  }
}
